// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useState } from "react"
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { AlertTriangle, RefreshCw } from "lucide-react"

interface ConvertToProspectModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
  customerName: string
  agreementCount: number
  invoiceCount?: number
  isLoading?: boolean
}

export function ConvertToProspectModal({
  isOpen,
  onClose,
  onConfirm,
  customerName,
  agreementCount,
  invoiceCount = 0,
  isLoading = false
}: ConvertToProspectModalProps) {
  const [isConfirming, setIsConfirming] = useState(false)

  const handleConfirm = async () => {
    try {
      setIsConfirming(true)
      await onConfirm()
    } finally {
      setIsConfirming(false)
    }
  }

  const handleClose = () => {
    if (isLoading || isConfirming) return
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box component="span" sx={{ color: "warning.main", display: "flex" }}>
          <AlertTriangle size={20} />
        </Box>
        Convert to Prospect
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ textAlign: "left" }}>
          Are you sure you want to convert <strong>{customerName}</strong> back to a prospect?
        </DialogContentText>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <Box sx={{ bgcolor: (t) => alpha(t.palette.warning.main, 0.12), border: 1, borderColor: (t) => alpha(t.palette.warning.main, 0.3), borderRadius: 1, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Box sx={{ color: "warning.main", mt: 0.25, flexShrink: 0, display: "flex" }}>
                <AlertTriangle size={16} />
              </Box>
              <Box sx={{ color: "warning.dark" }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  This action will permanently delete:
                </Typography>
                <Box component="ul" sx={{ pl: 2.5, listStyleType: "disc", "& li": { mb: 0.5 }, fontSize: "0.875rem" }}>
                  <li>
                    <strong>{agreementCount}</strong> agreement{agreementCount !== 1 ? 's' : ''}
                  </li>
                  {invoiceCount > 0 && (
                    <li>
                      <strong>{invoiceCount}</strong> invoice{invoiceCount !== 1 ? 's' : ''}
                    </li>
                  )}
                  <li>All related financial data</li>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ bgcolor: (t) => alpha(t.palette.info.main, 0.12), border: 1, borderColor: (t) => alpha(t.palette.info.main, 0.3), borderRadius: 1, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Box sx={{ color: "info.main", mt: 0.25, flexShrink: 0, display: "flex" }}>
                <RefreshCw size={16} />
              </Box>
              <Box sx={{ color: "info.dark" }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  After conversion:
                </Typography>
                <Typography variant="body2">
                  The customer will appear in your prospects list and can be managed as a potential customer again.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip
              label="Irreversible Action"
              size="small"
              color="error"
              sx={{ fontSize: "0.75rem" }}
            />
            <Chip
              label="Data Loss"
              size="small"
              sx={{ fontSize: "0.75rem" }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ gap: 1 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          disabled={isLoading || isConfirming}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={isLoading || isConfirming}
          {...((isLoading || isConfirming)
            ? { startIcon: <Box sx={{ display: "flex", animation: "spin 1s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } }}><RefreshCw size={16} /></Box> }
            : {})}
        >
          {isConfirming ? 'Converting...' : 'Yes, Convert to Prospect'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
